import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { useSEO } from "@/lib/useSEO";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Instagram, Facebook, Music2, MapPin, Image as ImageIcon, Check, X, ArrowRight, Quote } from "lucide-react";
import { StoryShareButton } from "@/components/StoryShare";

const TIKTOK_ICON = Music2; // visual approximation; lucide has no native TikTok glyph

const MAX_HEADLINE = 120;
const MAX_STORY = 5000;
const MAX_MORAL = 500;

function CharCounter({ value, max }) {
  const pct = Math.min(100, Math.round(((value || "").length / max) * 100));
  const over = (value || "").length > max;
  return (
    <div className={`text-[10px] uppercase tracking-widest font-bold ${over ? "text-red-400" : pct > 90 ? "text-amber-400" : "text-zinc-500"}`}>
      {(value || "").length}/{max}
    </div>
  );
}

function SocialIcon({ kind }) {
  if (kind === "instagram") return <Instagram className="h-3.5 w-3.5" />;
  if (kind === "facebook") return <Facebook className="h-3.5 w-3.5" />;
  if (kind === "tiktok") return <TIKTOK_ICON className="h-3.5 w-3.5" />;
  return null;
}

function StoryCard({ s }) {
  return (
    <article
      className="border border-[#27272A] bg-[#0F0F0F] overflow-hidden hover:border-[#FF331F]/50 transition-colors group relative"
      data-testid={`story-card-${s.id}`}
    >
      {/* Share button — top-right corner, always visible */}
      <div className="absolute top-3 right-3 z-10">
        <StoryShareButton story={s} className="bg-black/60 backdrop-blur-sm" />
      </div>

      {s.image_url && (
        <div className="relative aspect-[16/10] overflow-hidden">
          <img src={s.image_url} alt={s.headline} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F]/90 via-transparent to-transparent" />
        </div>
      )}
      <div className="p-6 md:p-8">
        <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-widest font-bold text-zinc-500">
          <span className="text-[#FF331F]">{s.user_name}</span>
          {s.city && (<><span>·</span><span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {s.city}</span></>)}
        </div>
        <Link to={`/story/${s.id}`} className="block group/title">
          <h2 className="font-display uppercase text-2xl md:text-3xl tracking-tight leading-tight mb-4 text-white group-hover/title:text-[#FF331F] transition-colors">{s.headline}</h2>
        </Link>
        <p className="text-zinc-300 text-sm leading-relaxed mb-5 whitespace-pre-wrap line-clamp-6">{s.story}</p>

        <div className="border-l-2 border-[#FF331F] pl-4 my-5">
          <div className="flex items-start gap-2 text-zinc-200 text-sm leading-relaxed italic">
            <Quote className="h-4 w-4 text-[#FF331F] flex-shrink-0 mt-1" />
            <p className="whitespace-pre-wrap">{s.moral}</p>
          </div>
        </div>

        {(s.social?.instagram || s.social?.tiktok || s.social?.facebook) && (
          <div className="flex items-center gap-2 pt-4 border-t border-[#27272A] flex-wrap">
            <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mr-1">Follow:</div>
            {["instagram", "tiktok", "facebook"].map((k) => {
              const url = s.social?.[k];
              if (!url) return null;
              const href = /^https?:\/\//i.test(url)
                ? url
                : (k === "instagram" ? `https://instagram.com/${url.replace(/^@/, "")}`
                  : k === "tiktok" ? `https://tiktok.com/@${url.replace(/^@/, "")}`
                  : `https://facebook.com/${url}`);
              return (
                <a
                  key={k}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-[#FF331F] border border-[#27272A] hover:border-[#FF331F] px-2 py-1"
                  data-testid={`story-${s.id}-social-${k}`}
                >
                  <SocialIcon kind={k} />
                  <span className="capitalize">{k}</span>
                </a>
              );
            })}
          </div>
        )}

        {/* "Read full story" deep link — also useful so users can land on the OG-tagged page */}
        <Link to={`/story/${s.id}`} className="inline-flex items-center text-[#FF331F] text-xs uppercase tracking-widest font-bold mt-5 hover:opacity-80" data-testid={`story-card-link-${s.id}`}>
          Read & share <ArrowRight className="ml-1 h-3 w-3" />
        </Link>
      </div>
    </article>
  );
}

function SubmissionForm({ user, onSubmitted }) {
  const [form, setForm] = useState({
    headline: "",
    story: "",
    moral: "",
    city: "",
    social: { instagram: "", tiktok: "", facebook: "" },
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadId, setUploadId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      toast.error("Image is over 12 MB — please pick a smaller one");
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/stories/upload-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadId(data.id);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
      setImageFile(null);
      setImagePreview(null);
      setUploadId(null);
    } finally {
      setUploadingImage(false);
    }
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setUploadId(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.headline.trim().length < 4) return toast.error("Headline must be at least 4 characters");
    if (form.story.trim().length < 40) return toast.error("Tell us a bit more — your story needs at least 40 characters");
    if (form.moral.trim().length < 4) return toast.error("Add the moral / takeaway of your story");

    setSubmitting(true);
    try {
      await api.post("/stories", {
        headline: form.headline.trim(),
        story: form.story.trim(),
        moral: form.moral.trim(),
        city: form.city.trim() || null,
        image_upload_id: uploadId || null,
        social: {
          instagram: form.social.instagram.trim() || null,
          tiktok: form.social.tiktok.trim() || null,
          facebook: form.social.facebook.trim() || null,
        },
      });
      toast.success("Submitted — Coach Chris will review and publish soon");
      setForm({ headline: "", story: "", moral: "", city: "", social: { instagram: "", tiktok: "", facebook: "" } });
      clearImage();
      onSubmitted?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-6 border border-[#27272A] bg-[#0F0F0F] p-6 md:p-10" data-testid="story-form">
      <div>
        <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-2">Submit your story</div>
        <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight">How has boxing changed your life?</h2>
        <p className="text-zinc-400 text-sm mt-2">
          Submitting as <span className="text-white font-bold">{user.full_name || user.email}</span>. Every story is reviewed before going live.
        </p>
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Headline</Label>
          <CharCounter value={form.headline} max={MAX_HEADLINE} />
        </div>
        <Input
          value={form.headline}
          onChange={(e) => setForm({ ...form, headline: e.target.value })}
          placeholder="e.g. Boxing pulled me out of the lowest year of my life"
          className="bg-[#050505] border-[#27272A] rounded-sm"
          maxLength={MAX_HEADLINE}
          required
          data-testid="story-headline"
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">City (optional)</Label>
        <Input
          value={form.city}
          onChange={(e) => setForm({ ...form, city: e.target.value })}
          placeholder="e.g. Brooklyn, NY"
          className="bg-[#050505] border-[#27272A] rounded-sm"
          maxLength={80}
          data-testid="story-city"
        />
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Your story</Label>
          <CharCounter value={form.story} max={MAX_STORY} />
        </div>
        <Textarea
          value={form.story}
          onChange={(e) => setForm({ ...form, story: e.target.value })}
          placeholder="Where were you before boxing? What changed? Health, discipline, self-defense — share what feels real."
          rows={10}
          className="bg-[#050505] border-[#27272A] rounded-sm leading-relaxed"
          maxLength={MAX_STORY}
          required
          data-testid="story-body"
        />
      </div>

      <div className="space-y-1.5 border-t border-[#27272A] pt-6">
        <div className="flex items-center justify-between">
          <Label className="text-[10px] uppercase tracking-widest text-[#FF331F] font-bold">Moral / takeaway *</Label>
          <CharCounter value={form.moral} max={MAX_MORAL} />
        </div>
        <p className="text-[11px] text-zinc-500 leading-relaxed">
          What do you want readers to walk away with? One or two sentences. This shows in big quote marks at the end of your story.
        </p>
        <Textarea
          value={form.moral}
          onChange={(e) => setForm({ ...form, moral: e.target.value })}
          placeholder="e.g. You don't have to be a fighter to fight for yourself. Show up — the rest follows."
          rows={3}
          className="bg-[#050505] border-[#27272A] rounded-sm leading-relaxed italic"
          maxLength={MAX_MORAL}
          required
          data-testid="story-moral"
        />
      </div>

      {/* Photo upload */}
      <div className="space-y-2 border-t border-[#27272A] pt-6">
        <Label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold inline-flex items-center gap-1.5">
          <ImageIcon className="h-3 w-3" /> Photo (optional)
        </Label>
        {!imagePreview && (
          <div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={onFileChange}
              className="block text-xs text-zinc-400 file:mr-3 file:py-2 file:px-4 file:rounded-sm file:border-0 file:text-xs file:font-bold file:uppercase file:tracking-widest file:bg-[#FF331F] file:text-white hover:file:bg-[#FF5242] cursor-pointer"
              data-testid="story-photo-input"
            />
            <p className="text-[11px] text-zinc-500 mt-2">Max 12 MB. JPEG or PNG. We auto-resize for you.</p>
          </div>
        )}
        {imagePreview && (
          <div className="relative inline-block">
            <img src={imagePreview} alt="preview" className="max-h-64 border border-[#27272A]" data-testid="story-photo-preview" />
            <button
              type="button"
              onClick={clearImage}
              className="absolute -top-2 -right-2 bg-[#FF331F] hover:bg-[#FF5242] text-white p-1 rounded-full"
              data-testid="story-photo-remove"
            >
              <X className="h-3 w-3" />
            </button>
            {uploadingImage && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-xs text-white uppercase tracking-widest">Uploading…</div>
            )}
            {!uploadingImage && uploadId && (
              <div className="absolute bottom-2 left-2 bg-emerald-500 text-black text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 inline-flex items-center gap-1">
                <Check className="h-3 w-3" /> Ready
              </div>
            )}
          </div>
        )}
      </div>

      {/* Social links */}
      <div className="space-y-3 border-t border-[#27272A] pt-6">
        <Label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Social links (optional)</Label>
        <p className="text-[11px] text-zinc-500">Drop your handles or full URLs. We&apos;ll link them under your story so readers can follow you.</p>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-[10px] text-zinc-400 inline-flex items-center gap-1"><Instagram className="h-3 w-3" /> Instagram</Label>
            <Input value={form.social.instagram} onChange={(e) => setForm({ ...form, social: { ...form.social, instagram: e.target.value } })} placeholder="@yourhandle" className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="story-social-instagram" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] text-zinc-400 inline-flex items-center gap-1"><TIKTOK_ICON className="h-3 w-3" /> TikTok</Label>
            <Input value={form.social.tiktok} onChange={(e) => setForm({ ...form, social: { ...form.social, tiktok: e.target.value } })} placeholder="@yourhandle" className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="story-social-tiktok" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] text-zinc-400 inline-flex items-center gap-1"><Facebook className="h-3 w-3" /> Facebook</Label>
            <Input value={form.social.facebook} onChange={(e) => setForm({ ...form, social: { ...form.social, facebook: e.target.value } })} placeholder="username or full URL" className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="story-social-facebook" />
          </div>
        </div>
      </div>

      <div className="border-t border-[#27272A] pt-6 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[11px] text-zinc-500 max-w-md">
          By submitting, you agree your story may be published on Better Through Boxing. We&apos;ll never share your email or contact info.
        </p>
        <Button
          type="submit"
          disabled={submitting || uploadingImage}
          className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm h-12 px-6"
          data-testid="story-submit"
        >
          {submitting ? "Submitting…" : "Submit story"} <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </form>
  );
}

export default function ShareYourStory() {
  useSEO({
    title: "Share Your Story · Better Through Boxing",
    description: "Boxing changed your life? Share your story and inspire the next person to start.",
    path: "/share-your-story",
  });
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/stories");
      setStories(data || []);
    } catch {
      setStories([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="App relative bg-[#050505]">
      <SiteHeader />
      <main data-testid="share-your-story-page">
        {/* Hero */}
        <section className="border-b border-[#27272A] py-16 md:py-24">
          <div className="max-w-4xl mx-auto px-6 md:px-10">
            <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-4">Community</div>
            <h1 className="font-display uppercase text-5xl md:text-7xl tracking-tight leading-[0.95] text-white mb-6">
              Share your story.
            </h1>
            <p className="text-zinc-300 text-base md:text-lg leading-relaxed max-w-2xl">
              Boxing pulled you out of something. Made you sharper. Gave you the discipline you didn&apos;t know you needed. Maybe even saved you in a real moment.
              Tell that story — and inspire the next person to throw their first punch.
            </p>
          </div>
        </section>

        {/* Submit form */}
        <section className="py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-6 md:px-10">
            {user ? (
              <SubmissionForm user={user} onSubmitted={load} />
            ) : (
              <div className="border border-[#27272A] bg-[#0F0F0F] p-8 md:p-12 text-center" data-testid="story-login-gate">
                <h2 className="font-display uppercase text-2xl md:text-3xl tracking-tight mb-3">Sign in to share your story.</h2>
                <p className="text-zinc-400 text-sm mb-6 max-w-md mx-auto">
                  We require accounts so every story is connected to a real person. It&apos;s free and takes 30 seconds.
                </p>
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  <Button onClick={() => navigate("/login?next=/share-your-story")} className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm h-11 px-5" data-testid="story-login-btn">
                    Sign in
                  </Button>
                  <Link to="/register?next=/share-your-story" className="text-xs uppercase tracking-widest font-bold text-zinc-400 hover:text-white" data-testid="story-register-link">
                    Create an account →
                  </Link>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Stories gallery */}
        <section className="py-12 md:py-16 border-t border-[#27272A]">
          <div className="max-w-6xl mx-auto px-6 md:px-10">
            <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-3">Read the stories</div>
            <h2 className="font-display uppercase text-3xl md:text-5xl tracking-tight mb-10">From the community.</h2>

            {loading ? (
              <div className="text-zinc-500 text-sm">Loading stories…</div>
            ) : stories.length === 0 ? (
              <div className="border border-dashed border-[#27272A] p-12 text-center" data-testid="story-empty">
                <p className="text-zinc-400 text-base mb-1">No stories yet.</p>
                <p className="text-zinc-500 text-sm">Be the first to share yours.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6 md:gap-8" data-testid="story-gallery">
                {stories.map((s) => <StoryCard key={s.id} s={s} />)}
              </div>
            )}
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
